import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { v4 as uuidv4 } from 'uuid';
import { DeviceTypeEnum, OnboardingStatusEnum, SSOProviderEnum } from '../../constants/auth.constant';
import { ILoginApiModel } from '../../models/login.model';
import { BaseStorageService } from './base-storage.service';

@Injectable({
    providedIn: 'root',
})
export class LoginService extends BaseStorageService {
    private readonly KEY_ACCESS_TOKEN = 'agmo-access-token';
    private readonly KEY_DEVICE = 'agmo-device';
    private readonly KEY_EMAIL = 'agmo-email';
    private readonly KEY_NAME = 'agmo-name';
    private readonly KEY_PROVIDER_TYPE = 'agmo-provider-type';
    private readonly KEY_REFRESH_TOKEN = 'agmo-refresh-token';
    private readonly KEY_ONBOARDING_STATUS = 'agmo-onboarding-status';
    private readonly KEY_USER_ID = 'agmo-user-id';
    private readonly KEY_COMPANY_INDUSTRY = 'agmo-company-industry';

    private userNameSubject = new BehaviorSubject<string>(this.getName());
    // eslint-disable-next-line @typescript-eslint/member-ordering
    userName$ = this.userNameSubject.asObservable();

    clear(): void {
        this.browserLocalStorage.removeItem(this.KEY_ACCESS_TOKEN);
        this.browserLocalStorage.removeItem(this.KEY_EMAIL);
        this.browserLocalStorage.removeItem(this.KEY_NAME);
        this.browserLocalStorage.removeItem(this.KEY_PROVIDER_TYPE);
        this.browserLocalStorage.removeItem(this.KEY_REFRESH_TOKEN);
        this.browserLocalStorage.removeItem(this.KEY_ONBOARDING_STATUS);
        this.browserLocalStorage.removeItem(this.KEY_USER_ID);
        this.browserLocalStorage.removeItem(this.KEY_COMPANY_INDUSTRY);
    }

    getDevice(): Device {
        const deviceJson = this.browserLocalStorage.getItem(this.KEY_DEVICE);
        let device: Device;

        try {
            device = JSON.parse(deviceJson || '') as Device;
        } catch {
            device = new Device();
        }

        this.browserLocalStorage.setItem(this.KEY_DEVICE, JSON.stringify(device));

        return device;
    }

    getAccessToken(): string | null {
        return this.browserLocalStorage.getItem(this.KEY_ACCESS_TOKEN) || null;
    }

    getEmail(): string {
        return this.browserLocalStorage.getItem(this.KEY_EMAIL) || '';
    }

    getName(): string {
        return this.browserLocalStorage.getItem(this.KEY_NAME) || '';
    }

    getProviderType(): SSOProviderEnum {
        const providerType = this.browserLocalStorage.getItem(this.KEY_PROVIDER_TYPE);
        if (providerType === null || providerType === undefined) {
            return SSOProviderEnum.Email;
        }

        return +providerType as SSOProviderEnum;
    }

    getOnboardingStatus(): OnboardingStatusEnum {
        const value = this.browserLocalStorage.getItem(this.KEY_ONBOARDING_STATUS);
        if (value === null || value === undefined) {
            return OnboardingStatusEnum.Completed;
        }

        return +value as OnboardingStatusEnum;
    }

    getRefreshToken(): string | null {
        return this.browserLocalStorage.getItem(this.KEY_REFRESH_TOKEN) || null;
    }

    getUserId(): string {
        return this.browserLocalStorage.getItem(this.KEY_USER_ID) || '';
    }

    getCompanyIndustry(): string {
        return this.browserLocalStorage.getItem(this.KEY_COMPANY_INDUSTRY) || '';
    }

    setFromLogin(login: ILoginApiModel): void {
        if (login.uiProviderType) {
            this.setProviderType(login.uiProviderType);
        }

        this.setAccessToken(login.accessToken);
        this.setEmail(login.email);
        this.updateName(login.fullName);
        this.setRefreshToken(login.refreshToken);
        this.setOnboardingStatus(login.onboardingStatus);
        this.setUserId(login.id);
        this.setCompanyIndustry(login.companyIndustry);
    }

    updateName(name: string): void {
        this.setName(name);
        this.userNameSubject.next(name);
    }

    private setAccessToken(accessToken: string): void {
        this.browserLocalStorage.setItem(this.KEY_ACCESS_TOKEN, accessToken);
    }

    private setEmail(email: string): void {
        this.browserLocalStorage.setItem(this.KEY_EMAIL, email);
    }

    private setName(name: string): void {
        this.browserLocalStorage.setItem(this.KEY_NAME, name);
    }

    private setProviderType(providerType = SSOProviderEnum.Email): void {
        this.browserLocalStorage.setItem(this.KEY_PROVIDER_TYPE, providerType.toString());
    }

    private setRefreshToken(refreshToken: string): void {
        this.browserLocalStorage.setItem(this.KEY_REFRESH_TOKEN, refreshToken);
    }

    private setOnboardingStatus(onboardingStatus = OnboardingStatusEnum.Completed): void {
        this.browserLocalStorage.setItem(this.KEY_ONBOARDING_STATUS, onboardingStatus.toString());
    }

    private setUserId(userId: string): void {
        this.browserLocalStorage.setItem(this.KEY_USER_ID, userId);
    }

    private setCompanyIndustry(companyIndustry: string): void {
        this.browserLocalStorage.setItem(this.KEY_COMPANY_INDUSTRY, companyIndustry);
    }
}

class Device {
    deviceNo = uuidv4();
    deviceType = DeviceTypeEnum.Web;
    guiVersion = 'v1.0.0';
    isIosSandBox = false;
    registrationId = 'randomRegistrationID';
}
